import React, { FC } from 'react';
import { Box, Card, Typography, Button, Skeleton, Tooltip } from '@mui/material';

export interface MetricCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ReactNode;
  accentClassName?: string;
  onAction?: () => void;
  actionLabel?: string;
  loading?: boolean;
  tooltip?: string;
  minHeight?: string | number;
}

const MetricCard: FC<MetricCardProps> = ({
  title,
  value,
  description,
  icon,
  accentClassName,
  onAction,
  actionLabel = 'View details',
  loading,
  tooltip,
  minHeight,
}) => {
  const cardContent = (
    <Card
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: '#fff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s',
        height: '100%',
        minHeight: minHeight,
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        },
      }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem', fontWeight: 500 }}>
          {title}
        </Typography>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            backgroundColor: accentClassName?.includes('sky')
              ? '#E0F2FE'
              : accentClassName?.includes('emerald')
              ? '#D1FAE5'
              : accentClassName?.includes('violet')
              ? '#EDE9FE'
              : accentClassName?.includes('amber')
              ? '#FEF3C7'
              : accentClassName?.includes('rose')
              ? '#FFE4E6'
              : '#F1F5F9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: accentClassName?.includes('sky')
              ? '#0369A1'
              : accentClassName?.includes('emerald')
              ? '#059669'
              : accentClassName?.includes('violet')
              ? '#7C3AED'
              : accentClassName?.includes('amber')
              ? '#D97706'
              : accentClassName?.includes('rose')
              ? '#E11D48'
              : 'text.primary',
          }}>
          {icon}
        </Box>
      </Box>
      {loading ? (
        <Box>
          <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="80%" height={16} />
        </Box>
      ) : (
        <>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
          {description && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              {description}
            </Typography>
          )}
        </>
      )}
      {onAction && (
        <Box sx={{ mt: 'auto', pt: 1.5 }}>
          <Button
            variant="text"
            size="small"
            onClick={onAction}
            sx={{
              textTransform: 'none',
              color: 'primary.main',
              fontSize: '0.8125rem',
              p: 0,
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}>
            {actionLabel}
          </Button>
        </Box>
      )}
    </Card>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow placement="top">
        {cardContent}
      </Tooltip>
    );
  }

  return cardContent;
};

export default MetricCard;
